import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react'
import { getModerationQueue, reviewModerationItem } from '../../services/api'
import type { ModerationItem } from '../../types'
import EmptyState from '../../components/shared/EmptyState'

type TabKey = 'pending' | 'approved' | 'rejected'

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'pending', label: 'Pending', icon: <Clock className="w-4 h-4" /> },
  { key: 'approved', label: 'Approved', icon: <CheckCircle className="w-4 h-4" /> },
  { key: 'rejected', label: 'Rejected', icon: <XCircle className="w-4 h-4" /> },
]

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Moderation() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabKey>('pending')
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})

  const { data: items, isLoading } = useQuery<ModerationItem[]>({
    queryKey: ['admin', 'moderation'],
    queryFn: getModerationQueue,
    refetchInterval: 30000,
  })

  const allItems = items ?? []

  const filteredItems = useMemo(() => {
    return allItems.filter((item) => item.status === activeTab)
  }, [allItems, activeTab])

  const pendingCount = allItems.filter((i) => i.status === 'pending').length
  const approvedToday = allItems.filter((i) => {
    if (i.status !== 'approved' || !i.reviewed_at) return false
    const today = new Date().toDateString()
    return new Date(i.reviewed_at).toDateString() === today
  }).length
  const rejectedToday = allItems.filter((i) => {
    if (i.status !== 'rejected' || !i.reviewed_at) return false
    const today = new Date().toDateString()
    return new Date(i.reviewed_at).toDateString() === today
  }).length

  const reviewMutation = useMutation({
    mutationFn: ({ itemId, decision }: { itemId: string; decision: 'approved' | 'rejected' }) =>
      reviewModerationItem(itemId, decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moderation'] })
    },
  })

  const handleReview = (itemId: string, decision: 'approved' | 'rejected') => {
    reviewMutation.mutate({ itemId, decision })
  }

  const handleNotesChange = (itemId: string, notes: string) => {
    setReviewNotes((prev) => ({ ...prev, [itemId]: notes }))
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-acad-500" />
          Content Moderation
        </h1>
        <p className="text-gray-500 mt-1">Review flagged content for classification decisions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
          <p className="text-sm text-amber-600">Pending</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{approvedToday}</p>
          <p className="text-sm text-green-600">Approved Today</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{rejectedToday}</p>
          <p className="text-sm text-red-600">Rejected Today</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {TABS.map((tab) => {
            const count = allItems.filter((i) => i.status === tab.key).length
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-acad-500 text-acad-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key
                      ? 'bg-acad-100 text-acad-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Items List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
              <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState
          title={
            activeTab === 'pending'
              ? 'No items pending review'
              : activeTab === 'approved'
              ? 'No approved items'
              : 'No rejected items'
          }
          description={
            activeTab === 'pending'
              ? 'All content has been reviewed. Check back later.'
              : `No items have been ${activeTab} yet.`
          }
          icon={activeTab === 'pending' ? CheckCircle : AlertTriangle}
        />
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <div
              key={item.item_id}
              className="bg-white rounded-lg shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                      {item.resource_type}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                      Borderline
                    </span>
                    <span className="text-xs text-gray-400">{formatDate(item.submitted_at)}</span>
                  </div>
                </div>
                {item.source_url && (
                  <a
                    href={item.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-acad-500 hover:text-acad-600 transition-colors ml-3"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>

              {/* Classification Reason */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Classification reason: </span>
                  {item.classification_reason}
                </p>
              </div>

              {/* Reviewed info for non-pending */}
              {item.status !== 'pending' && item.reviewed_by && (
                <div className="text-sm text-gray-500 mb-3">
                  Reviewed by <span className="font-medium">{item.reviewed_by}</span>
                  {item.reviewed_at && <span> on {formatDate(item.reviewed_at)}</span>}
                </div>
              )}

              {/* Decision area for pending items */}
              {item.status === 'pending' && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  <textarea
                    value={reviewNotes[item.item_id] ?? ''}
                    onChange={(e) => handleNotesChange(item.item_id, e.target.value)}
                    placeholder="Add review notes (optional)..."
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-acad-500 resize-none"
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleReview(item.item_id, 'approved')}
                      disabled={reviewMutation.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white
                                 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors
                                 disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleReview(item.item_id, 'rejected')}
                      disabled={reviewMutation.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white
                                 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors
                                 disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
